import type { OrderItem, Order } from '../graphql/query/getOrder';
import type { WorkflowData, WorkflowItemData } from '../workflow';
import type { ShipmentGroup } from './item';
import { parseItemKey, getBaseItemId } from './itemKey';
import { getItemsInSameShipment } from './shipmentScopeUtils';
import { hasAllResolutionsShipmentScope } from './reasonUtils';

/**
 * Helper to find compound key tracking number for an item
 */
const findCompoundKeyTrackingNumber = (
  itemId: string,
  data: WorkflowData | undefined,
): string | undefined => {
  const compoundKey = Object.keys(data?.items || {}).find((key) => {
    const { id } = parseItemKey(key);
    return id === itemId && key !== itemId;
  });

  if (!compoundKey) return undefined;

  return data?.items?.[compoundKey]?.itemSelection?.trackingNumber;
};

/**
 * Helper to get selected tracking number for an item (direct or compound)
 */
const getSelectedTrackingNumber = (
  itemId: string,
  selectedItemData: WorkflowItemData | undefined,
  hasCompoundKey: boolean,
  data: WorkflowData | undefined,
): string | undefined => {
  let selectedTrackingNumber = selectedItemData?.itemSelection?.trackingNumber;

  // If no direct tracking number and we have compound key, extract from there
  if (!selectedTrackingNumber && hasCompoundKey) {
    selectedTrackingNumber = findCompoundKeyTrackingNumber(itemId, data);
  }

  return selectedTrackingNumber;
};

/**
 * Helper to check if an item has compound keys
 */
const hasItemCompoundKey = (
  itemId: string,
  data: WorkflowData | undefined,
): boolean => {
  return Object.keys(data?.items || {}).some((key) => {
    const { id } = parseItemKey(key);
    return id === itemId && key !== itemId;
  });
};

/**
 * Helper to check if an item should be auto-added due to shipment scope reasons
 */
export const shouldItemBeAutoAdded = (
  item: OrderItem,
  shipmentGroup: ShipmentGroup,
  data: WorkflowData | undefined,
  order: Order | null,
): boolean => {
  // Check all selected items for shipment scope reasons
  for (const [selectedItemId, selectedItemData] of Object.entries(
    data?.items || {},
  )) {
    if (!selectedItemData?.reason?.reasonId) continue;

    // Find the order item to get its reasons
    // For compound item IDs, extract the base item ID to find the order item
    const baseSelectedItemId = getBaseItemId(selectedItemId);

    const orderItem = order?.items?.find((oi) => oi.id === baseSelectedItemId);
    if (!orderItem?.reasons) continue;

    // Find the selected reason
    const selectedReason = orderItem.reasons.find(
      (reason) => reason.code === selectedItemData.reason?.reasonId,
    );

    if (!selectedReason) continue;

    // Check if this reason has shipment scope
    if (hasAllResolutionsShipmentScope(selectedReason)) {
      // Get the tracking number from the selected item
      const selectedTrackingNumber =
        selectedItemData.itemSelection?.trackingNumber;

      // If this selected item is from the same shipment as the current item being evaluated
      if (selectedTrackingNumber === shipmentGroup.trackingNumber) {
        // Get items that would be auto-added from this shipment
        const shipmentItems = order
          ? getItemsInSameShipment(
              order,
              baseSelectedItemId,
              undefined,
              selectedTrackingNumber,
            )
          : [];

        // If the current item is in the same shipment as the selected item, it should be auto-added
        if (shipmentItems.some((shipmentItem) => shipmentItem.id === item.id)) {
          return true;
        }
      }
    }
  }

  return false;
};

/**
 * Helper to determine if an item instance should be excluded from selection
 */
export const shouldExcludeItemInstance = (
  item: OrderItem,
  shipmentGroup: ShipmentGroup,
  itemAppearanceCount: number,
  data: WorkflowData | undefined,
): boolean => {
  const selectedItemData = data?.items?.[item.id];

  const hasCompoundKey = hasItemCompoundKey(item.id, data);

  // Early return if item is not selected in any form
  if (!selectedItemData && !hasCompoundKey) {
    return false;
  }

  // Handle single-shipment items
  if (itemAppearanceCount === 1) {
    return true; // Single-shipment item that's already selected, exclude it
  }

  // Handle multi-shipment items with tracking number logic
  if (itemAppearanceCount > 1) {
    const selectedTrackingNumber = getSelectedTrackingNumber(
      item.id,
      selectedItemData,
      hasCompoundKey,
      data,
    );

    // Only exclude if this specific shipment instance is already selected
    return !!(
      selectedTrackingNumber &&
      selectedTrackingNumber === shipmentGroup.trackingNumber
    );
  }

  return false;
};

/**
 * Process items for selection availability in multiple shipment scenarios
 */
export const processShipmentItems = (
  shipmentGroups: ShipmentGroup[],
  eligibleItems: OrderItem[],
  data: WorkflowData | undefined,
  order: Order | null,
): OrderItem[] => {
  return shipmentGroups.flatMap((shipmentGroup) =>
    shipmentGroup.items
      .filter((item: OrderItem) =>
        // Must be eligible
        eligibleItems.some((eligibleItem) => eligibleItem.id === item.id),
      )
      .map((item) => {
        // Check if this item appears in multiple shipments
        const itemAppearanceCount = shipmentGroups.filter((group) =>
          group.items.some((groupItem: OrderItem) => groupItem.id === item.id),
        ).length;

        return { item, itemAppearanceCount };
      })
      .filter(
        ({ item, itemAppearanceCount }) =>
          // Exclude items that should be excluded or auto-added
          !shouldExcludeItemInstance(
            item,
            shipmentGroup,
            itemAppearanceCount,
            data,
          ) && !shouldItemBeAutoAdded(item, shipmentGroup, data, order),
      )
      .map(
        ({ item }) =>
          ({
            // Add shipment context to the item for proper tracking number resolution
            ...item,
            _shipmentTrackingNumber: shipmentGroup.trackingNumber,
          }) as OrderItem,
      ),
  );
};

/**
 * Process items that should be auto-added in multiple shipment scenarios
 */
export const processAutoAddedShipmentItems = (
  shipmentGroups: ShipmentGroup[],
  eligibleItems: OrderItem[],
  itemsToBeAutoAdded: string[],
  data: WorkflowData | undefined,
  order: Order | null,
): OrderItem[] =>
  shipmentGroups.flatMap((shipmentGroup) =>
    // Filter eligible items in this shipment that should be auto-added
    shipmentGroup.items.filter(
      (item: OrderItem) =>
        eligibleItems.some((eligibleItem) => eligibleItem.id === item.id) &&
        itemsToBeAutoAdded.includes(item.id) &&
        shouldItemBeAutoAdded(item, shipmentGroup, data, order),
    ),
  );
